import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import SelectedIcon from 'src/components/selected-icon'
import React from 'react'
import { Typography } from '@material-ui/core'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'

// Constants - Updated
export const DEFAULT_HOTSPOT_BACKGROUND = '#007cba'
export const DEFAULT_HOTSPOT_ICON_COLOR = '#ffffff'
export const DEFAULT_HOTSPOT_ICON = 'mdi:plus'

// Hotspot Update Function - Updated to immediately apply changes like ButtonOptionsModal
export const handleHotspotUpdate = (editor, hotspotId, hotspotProps) => {
  // Find and update the hotspot model immediately
  editor.model.change(writer => {
    const root = editor.model.document.getRoot()
    const hotspot = findHotspotById(root, hotspotId)

    if (hotspot) {
      // Update all properties
      if (hotspotProps.backgroundColor !== undefined) {
        writer.setAttribute(
          'backgroundColor',
          hotspotProps.backgroundColor,
          hotspot,
        )
      }
      if (hotspotProps.iconColor !== undefined) {
        writer.setAttribute('iconColor', hotspotProps.iconColor, hotspot)
      }
      if (hotspotProps.icon !== undefined) {
        writer.setAttribute('icon', hotspotProps.icon, hotspot)
      }
      if (hotspotProps.iconName !== undefined) {
        writer.setAttribute('iconName', hotspotProps.iconName, hotspot)
      }

      // Force re-render immediately
      const container = hotspot.parent
      editor.editing.reconvertItem(container)
      editor.editing.view.forceRender()
    }
  })
}

// Helper function to find hotspot by ID
const findHotspotById = (element, id) => {
  if (element.name === 'imageHotspot' && element.getAttribute('id') === id) {
    return element
  }

  if (element.getChildren) {
    for (const child of element.getChildren()) {
      const result = findHotspotById(child, id)
      if (result) return result
    }
  }

  return null
}

// Color Handlers - Updated
export const handleHotspotBackgroundColorChange = (
  newColor,
  { editor, hotspotId, iconColor, selectedIcon, selectedIconName },
) => {
  handleHotspotUpdate(editor, hotspotId, {
    backgroundColor: newColor,
    iconColor: iconColor || DEFAULT_HOTSPOT_ICON_COLOR,
    icon: selectedIcon || '',
    iconName: selectedIconName || DEFAULT_HOTSPOT_ICON,
  })
}

export const handleHotspotIconColorChange = (
  newColor,
  { editor, hotspotId, backgroundColor, selectedIcon, selectedIconName },
) => {
  handleHotspotUpdate(editor, hotspotId, {
    backgroundColor: backgroundColor || DEFAULT_HOTSPOT_BACKGROUND,
    iconColor: newColor,
    icon: selectedIcon || '',
    iconName: selectedIconName || DEFAULT_HOTSPOT_ICON,
  })
}

// Icon Handlers - Updated
export const handleHotspotIconSelect = (
  icon,
  {
    editor,
    hotspotId,
    backgroundColor,
    iconColor,
    setSelectedIcon,
    setSelectedIconName,
  },
) => {
  setSelectedIcon(icon)
  setSelectedIconName(icon)

  handleHotspotUpdate(editor, hotspotId, {
    backgroundColor: backgroundColor || DEFAULT_HOTSPOT_BACKGROUND,
    iconColor: iconColor || DEFAULT_HOTSPOT_ICON_COLOR,
    icon: icon,
    iconName: icon,
  })
}

export const handleHotspotIconDelete = ({
  editor,
  hotspotId,
  backgroundColor,
  iconColor,
  setSelectedIcon,
  setSelectedIconName,
}) => {
  setSelectedIcon('')
  setSelectedIconName(DEFAULT_HOTSPOT_ICON)

  handleHotspotUpdate(editor, hotspotId, {
    backgroundColor: backgroundColor || DEFAULT_HOTSPOT_BACKGROUND,
    iconColor: iconColor || DEFAULT_HOTSPOT_ICON_COLOR,
    icon: '',
    iconName: DEFAULT_HOTSPOT_ICON,
  })
}

export const showHotspotIconSelector = ({
  editor,
  target,
  handleIconSelect,
  dispatch,
}) => {
  const handleClickOutside = event => {
    const messageBox = document.getElementById('message-box-1')
    if (messageBox && !messageBox.contains(event.target)) {
      document.removeEventListener('mousedown', handleClickOutside)
      dispatch(messageBoxHideAction('message-box-1'))
    }
  }

  document.addEventListener('mousedown', handleClickOutside)

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
            <Typography>Select Icon</Typography>
          </div>`,
        buttons: [],
        component: (
          <SelectedIcon
            editor={editor}
            target={target}
            onIconSelect={handleIconSelect}
          />
        ),
        variant: MessageBoxVariant.DRAGGABLE,
        callback: () => {
          document.removeEventListener('mousedown', handleClickOutside)
        },
      }),
    ),
  )
}

// Delete Handler
export const deleteHotspot = (editor, hotspotId) => {
  editor.execute('removeImageHotspot', hotspotId)
}

// Get Initial State - Updated to include new attributes
export const getInitialHotspotState = selection => {
  if (selection) {
    return {
      backgroundColor:
        selection.getAttribute('backgroundColor') || DEFAULT_HOTSPOT_BACKGROUND,
      iconColor:
        selection.getAttribute('iconColor') || DEFAULT_HOTSPOT_ICON_COLOR,
      selectedIcon: selection.getAttribute('icon') || '',
      selectedIconName:
        selection.getAttribute('iconName') || DEFAULT_HOTSPOT_ICON,
      hotspotId: selection.getAttribute('id') || '',
    }
  }

  return {
    backgroundColor: DEFAULT_HOTSPOT_BACKGROUND,
    iconColor: DEFAULT_HOTSPOT_ICON_COLOR,
    selectedIcon: '',
    selectedIconName: DEFAULT_HOTSPOT_ICON,
    hotspotId: '',
  }
}

// New helper function to update hotspot visual appearance
export const updateHotspotVisualStyle = (editor, hotspotId, styleProps) => {
  // DOM'da hotspot elementini bul ve görsel olarak güncelle
  setTimeout(() => {
    const hotspotElement = document.querySelector(`[data-id="${hotspotId}"]`)
    if (hotspotElement) {
      const iconElement = hotspotElement.querySelector('.hotspot-icon')
      if (iconElement && styleProps) {
        // Background color update
        if (styleProps.backgroundColor) {
          iconElement.style.backgroundColor = styleProps.backgroundColor
        }

        // Icon color update
        if (styleProps.iconColor) {
          iconElement.style.color = styleProps.iconColor
          iconElement.style.borderColor = styleProps.iconColor

          // SVG içindeki fill attribute'unu güncelle
          const svgElement = iconElement.querySelector('svg path')
          if (svgElement) {
            svgElement.setAttribute('fill', styleProps.iconColor)
          }

          // Iconify icon varsa color style'ını güncelle
          const iconifyIcon = iconElement.querySelector('iconify-icon')
          if (iconifyIcon) {
            iconifyIcon.style.color = styleProps.iconColor
          }
        }

        // Icon update
        if (styleProps.icon && styleProps.iconName) {
          const existingIcon = iconElement.querySelector('iconify-icon')
          const existingSvg = iconElement.querySelector('svg')

          if (existingIcon) existingIcon.remove()
          if (existingSvg) existingSvg.remove()

          if (styleProps.icon) {
            const newIcon = document.createElement('iconify-icon')
            newIcon.setAttribute('icon', styleProps.icon)
            newIcon.style.color =
              styleProps.iconColor || DEFAULT_HOTSPOT_ICON_COLOR
            newIcon.style.width = '16px'
            newIcon.style.height = '16px'
            iconElement.appendChild(newIcon)
          }
        }
      }
    }
  }, 50)
}
